import type { SalesLinesFilter, SalesMonth, Stream } from './api';

/**
 * Sums and ratios of the API's decimal strings, done in whole sen (BigInt) so
 * money never becomes a float on the way to the screen (CLAUDE.md §7.3).
 */
export function toSen(value: string): bigint {
  const negative = value.startsWith('-');
  const [whole = '0', fraction = ''] = value.replace('-', '').split('.');
  const sen = BigInt(whole === '' ? '0' : whole) * 100n + BigInt(`${fraction}00`.slice(0, 2));
  return negative ? -sen : sen;
}

export function fromSen(sen: bigint): string {
  const negative = sen < 0n;
  const abs = negative ? -sen : sen;
  const fraction = (abs % 100n).toString().padStart(2, '0');
  return `${negative ? '-' : ''}${(abs / 100n).toString()}.${fraction}`;
}

/** A stream's revenue over the year, from the months the summary returns. */
export function streamTotal(months: SalesMonth[], stream: Stream): string {
  return fromSen(months.reduce((sum, m) => sum + toSen(m.byStream[stream].revenue), 0n));
}

/**
 * The change from last year as a ratio (0.235 is +23.5%), or null when last
 * year had nothing to compare with. Exact to a thousandth of a percent before
 * it becomes a number, and it only becomes one to be formatted.
 */
export function changeRatio(current: string, lastYear: string): number | null {
  const last = toSen(lastYear);
  if (last <= 0n) return null;
  const hundredThousandths = ((toSen(current) - last) * 100_000n) / last;
  return Number(hundredThousandths) / 100_000;
}

export function formatPercent(ratio: number, language: string, signed = false): string {
  return new Intl.NumberFormat(language, {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    ...(signed ? { signDisplay: 'exceptZero' as const } : {}),
  }).format(ratio);
}

/** A month's name in the reader's language: "Jan", or "January" in full. */
export function monthName(month: number, language: string, width: 'short' | 'long' = 'short'): string {
  return new Intl.DateTimeFormat(language, { month: width, timeZone: 'UTC' }).format(
    new Date(Date.UTC(2000, month - 1, 1)),
  );
}

/**
 * A decimal string as a number, for a chart axis and nothing else. ECharts
 * plots numbers; every figure a person reads is formatted from the string.
 */
export function plotValue(value: string): number {
  return Number(value);
}

/** Units in thousandths (BigInt): the API sends quantities to three places. */
function toMilli(value: string): bigint {
  const negative = value.startsWith('-');
  const [whole = '0', fraction = ''] = value.replace('-', '').split('.');
  const milli = BigInt(whole === '' ? '0' : whole) * 1000n + BigInt(`${fraction}000`.slice(0, 3));
  return negative ? -milli : milli;
}

function fromMilli(milli: bigint): string {
  const negative = milli < 0n;
  const abs = negative ? -milli : milli;
  return `${negative ? '-' : ''}${(abs / 1000n).toString()}.${(abs % 1000n).toString().padStart(3, '0')}`;
}

/** A quantity string for a person: '2.000' reads 2, '1.500' reads 1.5. Never a float. */
export function formatUnits(value: string): string {
  const negative = value.startsWith('-');
  const [whole = '0', fraction = ''] = value.replace('-', '').split('.');
  const grouped = (whole === '' ? '0' : whole).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const trimmed = fraction.replace(/0+$/, '');
  return `${negative ? '-' : ''}${grouped}${trimmed === '' ? '' : `.${trimmed}`}`;
}

/** One stack in the sold-bikes chart: a brand, no brand, or all the rest. */
export interface BrandSeries {
  /** Stable series id for the chart. */
  id: string;
  kind: 'brand' | 'other';
  /** The brand as the API sent it; null is the lines with no brand. Unused for 'other'. */
  brand: string | null;
  /** Units per month (1..12) as decimal strings. */
  units: Map<number, string>;
}

export const OTHER_BRANDS_ID = 'OTHER_BRANDS';
const NO_BRAND_ID = 'BRAND_NONE';

export const brandSeriesId = (brand: string | null): string =>
  brand === null ? NO_BRAND_ID : `BRAND:${brand}`;

/**
 * The `top` brands by units over the year, in that order, then one series for
 * every other brand. The API's monthly units are summed here in thousandths;
 * nothing else is computed.
 */
export function foldBrands(
  sold: { month: number; brand: string | null; units: string }[],
  top = 5,
): BrandSeries[] {
  const totals = new Map<string, { brand: string | null; milli: bigint }>();
  for (const row of sold) {
    const id = brandSeriesId(row.brand);
    const entry = totals.get(id) ?? { brand: row.brand, milli: 0n };
    entry.milli += toMilli(row.units);
    totals.set(id, entry);
  }
  const ranked = [...totals.entries()].sort(([aId, a], [bId, b]) =>
    a.milli === b.milli ? aId.localeCompare(bId) : a.milli > b.milli ? -1 : 1,
  );
  const keep = new Set(ranked.slice(0, top).map(([id]) => id));

  const series = new Map<string, { s: BrandSeries; milli: Map<number, bigint> }>();
  const slot = (id: string, kind: 'brand' | 'other', brand: string | null) => {
    const found = series.get(id);
    if (found !== undefined) return found;
    const created = {
      s: { id, kind, brand, units: new Map<number, string>() },
      milli: new Map<number, bigint>(),
    };
    series.set(id, created);
    return created;
  };
  for (const [id, { brand }] of ranked.slice(0, top)) slot(id, 'brand', brand);
  for (const row of sold) {
    const id = brandSeriesId(row.brand);
    const target = keep.has(id) ? slot(id, 'brand', row.brand) : slot(OTHER_BRANDS_ID, 'other', null);
    target.milli.set(row.month, (target.milli.get(row.month) ?? 0n) + toMilli(row.units));
  }
  return [...series.values()].map(({ s, milli }) => {
    for (const [month, value] of milli) s.units.set(month, fromMilli(value));
    return s;
  });
}

/**
 * The Sales lines behind one bar of the sold-bikes chart. A brand goes through
 * as the API named it. No brand asks for lines with no item group, which the
 * Sales drill-down takes as an empty string. Other brands has no single group
 * to ask for, so it opens the month's bikes.
 */
export function soldBikesFilter(year: number, month: number, series: BrandSeries): SalesLinesFilter {
  if (series.kind === 'other') return { year, month, stream: 'BIKES' };
  return { year, month, stream: 'BIKES', itemGroup: series.brand ?? '' };
}

/** Money strings added in whole sen: the API's figures, never floats. */
export function sumMoney(values: string[]): string {
  return fromSen(values.reduce((sum, v) => sum + toSen(v), 0n));
}

/** Quantity strings added in thousandths. */
export function sumUnits(values: string[]): string {
  return fromMilli(values.reduce((sum, v) => sum + toMilli(v), 0n));
}
